import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ShoppingCart, Search, Plus, Minus, Trash2, Printer, 
  Loader2, User, Package, Check, X, FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { SellerDashboardLayout } from "./Dashboard";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { feeForZone } from "../../lib/deliveryPricing";
import CnomUsurpationNotice from "../../components/CnomUsurpationNotice";
import { toastApiError } from "../../lib/toastApiError";

const POSPage = () => {
  const { t } = useTranslation("seller");
  const { t: tn } = useTranslation("nav");
  const { user } = useAuth();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [forDelivery, setForDelivery] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryZoneId, setDeliveryZoneId] = useState("");
  const [deliveryPricing, setDeliveryPricing] = useState({ fallback_price: 0, zones: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    fetchInventory();
    apiClient.get("/delivery/pricing")
      .then((res) => setDeliveryPricing(res.data || { fallback_price: 0, zones: [] }))
      .catch(() => {});
    // Focus on search input
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredInventory(inventory);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredInventory(
        inventory.filter(item => 
          item.medicine?.name?.toLowerCase().includes(query) ||
          item.medicine?.molecule?.toLowerCase().includes(query) ||
          item.medicine?.brand?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, inventory]);

  const fetchInventory = async () => {
    try {
      const res = await apiClient.get("/pharmacy/inventory");
      setInventory(res.data.filter(item => item.quantity > 0));
      setFilteredInventory(res.data.filter(item => item.quantity > 0));
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency',
      currency: 'CDF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const addToCart = (item) => {
    const existingIndex = cart.findIndex(c => c.medicine_id === item.medicine_id);
    
    if (existingIndex >= 0) {
      // Check stock
      if (cart[existingIndex].quantity >= item.quantity) {
        toast.error(t("pos.toastInsufficientStock"));
        return;
      }
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        medicine_id: item.medicine_id,
        medicine_name: item.medicine?.name || t("pos.productFallback"),
        quantity: 1,
        unit_price: item.price || 0,
        max_stock: item.quantity,
        requires_prescription: item.medicine?.requires_prescription || false
      }]);
    }
    toast.success(t("pos.toastAddedToCart", { name: item.medicine?.name }));
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    const newQuantity = newCart[index].quantity + delta;
    
    if (newQuantity <= 0) {
      newCart.splice(index, 1);
    } else if (newQuantity > newCart[index].max_stock) {
      toast.error(t("pos.toastInsufficientStock"));
      return;
    } else {
      newCart[index].quantity = newQuantity;
    }
    
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmitSale = async () => {
    if (cart.length === 0) {
      toast.error(t("pos.toastEmptyCart"));
      return;
    }
    setShowConfirmDialog(true);
  };

  const confirmSale = async () => {
    if (forDelivery && !deliveryAddress.trim()) {
      toast.error(t("pos.toastEnterAddress"));
      return;
    }
    if (forDelivery && !clientPhone.trim()) {
      toast.error(t("pos.toastEnterPhone"));
      return;
    }
    setSubmitting(true);
    try {
      const saleData = {
        items: cart.map(item => ({
          medicine_id: item.medicine_id,
          medicine_name: item.medicine_name,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
        client_name: clientName || null,
        client_phone: clientPhone || null,
        for_delivery: forDelivery,
        delivery_address: forDelivery ? deliveryAddress : null,
        delivery_zone_id: forDelivery ? (deliveryZoneId || null) : null,
      };

      const res = await apiClient.post("/pos/sale", saleData);
      setLastSale(res.data);
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      
      // Reset cart
      setCart([]);
      setClientName("");
      setClientPhone("");
      setForDelivery(false);
      setDeliveryAddress("");
      setDeliveryZoneId("");
      
      // Refresh inventory
      fetchInventory();
      
      toast.success(t("pos.toastSaleSuccess"));
    } catch (error) {
      console.error("Error submitting sale:", error);
      toastApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintReceipt = () => {
    if (lastSale?.id) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pos/invoice/${lastSale.id}/pdf`, '_blank');
    }
  };

  if (loading) {
    return (
      <SellerDashboardLayout title={tn("seller.pos")}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500" />
        </div>
      </SellerDashboardLayout>
    );
  }

  return (
    <SellerDashboardLayout title={tn("seller.pos")}>
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Product Search & Selection */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search Bar */}
          <Card className="border-0 shadow-soft">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  ref={searchRef}
                  type="text"
                  placeholder={t("pos.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-lg"
                  data-testid="pos-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Products Grid */}
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5 text-omu-blue-500" />
                {t("pos.productsAvailable", { count: filteredInventory.length })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto">
                {filteredInventory.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => addToCart(item)}
                    className={`p-4 border rounded-xl cursor-pointer hover:border-omu-blue-300 hover:bg-omu-blue-50 transition-all ${
                      item.medicine?.requires_prescription 
                        ? "border-amber-300 bg-amber-50/30" 
                        : "border-slate-200"
                    }`}
                    data-testid={`product-${item.medicine_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-slate-900 truncate flex-1">
                        {item.medicine?.name}
                      </h4>
                      {item.medicine?.requires_prescription && (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 flex-shrink-0" title={t("pos.prescriptionRequired")}>
                          <FileText className="h-3 w-3 mr-0.5" />
                          {t("pos.prescriptionShort")}
                        </Badge>
                      )}
                    </div>
                    {item.medicine?.dosage && (
                      <p className="text-xs text-slate-500 mt-0.5">{item.medicine.dosage}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-omu-blue-600 font-semibold">
                        {formatCurrency(item.price)}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {t("pos.stock", { qty: item.quantity })}
                      </Badge>
                    </div>
                  </div>
                ))}
                {filteredInventory.length === 0 && (
                  <div className="col-span-full text-center py-8 text-slate-500">
                    {t("pos.noProductsFound")}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cart Panel */}
        <div className="space-y-4">
          {/* Client Info */}
          <Card className="border-0 shadow-soft">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-omu-blue-500" />
                {t("pos.clientInfoOptional")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                placeholder={t("pos.clientName")}
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                data-testid="client-name"
              />
              <Input
                placeholder={t("pos.clientPhone")}
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                data-testid="client-phone"
              />
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="mt-1" checked={forDelivery} onChange={(e) => setForDelivery(e.target.checked)} />
                <span>{t("pos.forDeliveryOmu")}</span>
              </label>
              {forDelivery && (
                <>
                  <select
                    className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                    value={deliveryZoneId}
                    onChange={(e) => setDeliveryZoneId(e.target.value)}
                  >
                    <option value="">
                      {t("pos.zoneOther", { price: (deliveryPricing.fallback_price || 0).toLocaleString() })}
                    </option>
                    {(deliveryPricing.zones || []).map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name} — {Number(zone.price).toLocaleString()} CDF
                      </option>
                    ))}
                  </select>
                  <Input
                    placeholder={t("pos.deliveryAddress")}
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Cart */}
          <Card className="border-0 shadow-soft">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-omu-blue-500" />
                {t("pos.cartCount", { count: cart.length })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cart.length === 0 ? (
                <p className="text-center text-slate-500 py-6">
                  {t("pos.clickToAdd")}
                </p>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {cart.map((item, index) => (
                    <div key={index} className={`flex items-center gap-2 p-2 rounded-lg ${
                      item.requires_prescription ? "bg-amber-50 border border-amber-200" : "bg-slate-50"
                    }`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className="font-medium text-sm truncate">{item.medicine_name}</p>
                          {item.requires_prescription && (
                            <FileText className="h-3 w-3 text-amber-600 flex-shrink-0" title={t("pos.prescriptionRequired")} />
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{formatCurrency(item.unit_price)} x {item.quantity}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateCartQuantity(index, -1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateCartQuantity(index, 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-700"
                          onClick={() => removeFromCart(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Total */}
              <div className="border-t border-slate-200 mt-4 pt-4 space-y-1">
                {forDelivery && (
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>{t("pos.deliveryLine", { zone: feeForZone(deliveryPricing, deliveryZoneId).zoneName })}</span>
                    <span>{formatCurrency(feeForZone(deliveryPricing, deliveryZoneId).fee)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-lg font-bold">
                  <span>{t("pos.total")}</span>
                  <span className="text-omu-blue-600">
                    {formatCurrency(calculateTotal() + (forDelivery ? feeForZone(deliveryPricing, deliveryZoneId).fee : 0))}
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                className="w-full mt-4 h-12 bg-omu-blue-500 hover:bg-omu-blue-600 text-lg font-semibold rounded-xl"
                onClick={handleSubmitSale}
                disabled={cart.length === 0 || submitting}
                data-testid="submit-sale-btn"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Check className="h-5 w-5 mr-2" />
                )}
                {t("pos.validateSale")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pos.confirmSale")}</DialogTitle>
            <DialogDescription>
              {t("pos.confirmDesc")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {clientName && (
              <p className="text-sm"><strong>{t("pos.clientLabel")}</strong> {clientName}</p>
            )}
            
            {/* Alert for prescription items */}
            {cart.some(item => item.requires_prescription) && (
              <>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <FileText className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    {t("pos.prescriptionAlert")}
                  </div>
                </div>
                <CnomUsurpationNotice />
              </>
            )}
            
            <div className="border rounded-lg p-3 bg-slate-50">
              {cart.map((item, i) => (
                <div key={i} className={`flex justify-between text-sm py-1 ${item.requires_prescription ? "text-amber-700" : ""}`}>
                  <span className="flex items-center gap-1">
                    {item.medicine_name} x{item.quantity}
                    {item.requires_prescription && <FileText className="h-3 w-3" />}
                  </span>
                  <span className="font-medium">{formatCurrency(item.quantity * item.unit_price)}</span>
                </div>
              ))}
              <div className="border-t mt-2 pt-2 space-y-1">
                {forDelivery && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>{t("pos.deliveryLine", { zone: feeForZone(deliveryPricing, deliveryZoneId).zoneName })}</span>
                    <span>{formatCurrency(feeForZone(deliveryPricing, deliveryZoneId).fee)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold">
                  <span>{t("pos.total")}</span>
                  <span className="text-omu-blue-600">
                    {formatCurrency(calculateTotal() + (forDelivery ? feeForZone(deliveryPricing, deliveryZoneId).fee : 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              {t("actions.cancel")}
            </Button>
            <Button 
              className="bg-omu-blue-500 hover:bg-omu-blue-600"
              onClick={confirmSale}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="h-6 w-6" />
              {t("pos.saleSuccess")}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 text-center">
            <p className="text-2xl font-bold text-slate-900 mb-2">
              {lastSale?.invoice_number}
            </p>
            <p className="text-slate-600">
              {t("pos.total")}: <span className="font-semibold text-omu-blue-600">{formatCurrency(lastSale?.grand_total ?? lastSale?.total)}</span>
            </p>
            {lastSale?.delivery_fee > 0 && (
              <p className="text-sm text-slate-500 mt-1">
                {t("pos.includingDelivery", { amount: formatCurrency(lastSale.delivery_fee) })}
                {lastSale.delivery_zone_name ? ` (${lastSale.delivery_zone_name})` : ""}
              </p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowSuccessDialog(false)}
              className="w-full sm:w-auto"
            >
              {t("actions.close")}
            </Button>
            <Button 
              className="w-full sm:w-auto bg-omu-blue-500 hover:bg-omu-blue-600"
              onClick={handlePrintReceipt}
            >
              <Printer className="h-4 w-4 mr-2" />
              {t("pos.printReceipt")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerDashboardLayout>
  );
};

export default POSPage;
